// [HOOK] Détection et gestion du processus sample Employee Onboarding
// [RÔLE] Vérifier si l'utilisateur a le processus sample et déclencher le tutoriel guidé
// [ARCHITECTURE] React hook avec API call + localStorage + tutoriel integration

import { useState, useEffect, useCallback } from 'react';
import { FamlaServices } from '../services';
import { useAppSelector } from '../stores';

interface SampleProcessState {
  hasSampleProcess: boolean;
  processId: string | null;
  isLoading: boolean;
  hasChecked: boolean;
  error: string | null;
}

export const useSampleProcess = () => {
  // [REDUX] Récupérer les données d'authentification depuis le store Redux
  const users = useAppSelector((state) => state.auth);
  
  const [state, setState] = useState<SampleProcessState>({
    hasSampleProcess: false,
    processId: null,
    isLoading: true,
    hasChecked: false,
    error: null
  });

  // [CHECK API] Vérifier si le processus sample existe via l'API
  const checkSampleProcess = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      
      // [TOKEN] Récupérer le token d'authentification depuis Redux
      const token = users.access_token;
      
      // [MÉTHODE 1] Chercher dans les processus de l'utilisateur
      const processResponse = await FamlaServices("api_host", "/process/my", "GET", "", token || "");
      
      if (processResponse.status === 200 && processResponse.body?.data) {
        const processes = processResponse.body.data;
        
        // [MÉTHODE 1] Chercher un processus avec l'attribut is_sample = true
        let sampleProcess = processes.find((process: any) => process.is_sample === true);
        
        // [FALLBACK] Si pas d'attribut is_sample, chercher par nom (rétrocompatibilité)
        if (!sampleProcess) {
          sampleProcess = processes.find((process: any) => {
            const name = process.name?.toLowerCase() || process.title?.toLowerCase() || '';
            return name.includes('employee') && name.includes('onboarding');
          });
        }
        
        if (sampleProcess) {
          setState({
            hasSampleProcess: true,
            processId: sampleProcess._id,
            isLoading: false,
            hasChecked: true,
            error: null
          });
          
          // [CACHE] Sauvegarder l'ID du processus sample
          localStorage.setItem('famla_sample_process_id', sampleProcess._id);
          console.log('✅ [SAMPLE PROCESS] Found sample process:', {
            id: sampleProcess._id,
            name: sampleProcess.name || sampleProcess.title,
            is_sample: sampleProcess.is_sample
          });
          return;
        }
      }
      
      // [MÉTHODE 2] Vérifier le cache localStorage
      const cachedProcessId = localStorage.getItem('famla_sample_process_id');
      if (cachedProcessId) {
        setState({
          hasSampleProcess: true,
          processId: cachedProcessId,
          isLoading: false,
          hasChecked: true,
          error: null
        });
        console.log('✅ [SAMPLE PROCESS] Found cached process ID:', cachedProcessId);
        return;
      }
      
      // [AUCUN PROCESSUS TROUVÉ]
      setState({
        hasSampleProcess: false,
        processId: null,
        isLoading: false,
        hasChecked: true,
        error: null
      });
      console.log('❌ [SAMPLE PROCESS] No Employee Onboarding process found');
      
    } catch (error: any) {
      console.error('❌ [SAMPLE PROCESS] Error checking sample process:', error);
      setState({
        hasSampleProcess: false,
        processId: null,
        isLoading: false,
        hasChecked: true,
        error: error.message || 'Error checking sample process'
      });
    }
  }, [users.access_token]);

  // [REFRESH] Fonction pour rafraîchir la vérification
  const refreshSampleProcess = useCallback(async () => {
    localStorage.removeItem('famla_sample_process_id');
    await checkSampleProcess();
  }, [checkSampleProcess]);

  // [RESET] Fonction pour réinitialiser le cache
  const resetSampleProcessCache = useCallback(() => {
    localStorage.removeItem('famla_sample_process_id');
    setState({
      hasSampleProcess: false,
      processId: null,
      isLoading: false,
      hasChecked: false,
      error: null
    });
  }, []);

  // [EFFECT] Vérifier au montage du composant
  useEffect(() => {
    if (users.access_token && !state.hasChecked) {
      checkSampleProcess();
    }
  }, [users.access_token, state.hasChecked, checkSampleProcess]);

  return {
    hasSampleProcess: state.hasSampleProcess,
    processId: state.processId,
    isLoading: state.isLoading,
    hasChecked: state.hasChecked,
    error: state.error,
    checkSampleProcess,
    refreshSampleProcess,
    resetSampleProcessCache
  };
};

export default useSampleProcess;
